import type {
  LazyResolver,
  TidalSandbox,
  TidalSandboxApi,
  TidalSandboxGlobalControlsConfig,
} from '@workwave-tidal/sandbox-utils'
import { useCallback, useEffect } from 'react'
import { type NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { proxy, snapshot, subscribe, useSnapshot } from 'valtio'

let setRouterLocation: NavigateFunction | undefined

type UrlParams = 'nokeybindings' | 'fs' | 'collapsed' | 'disabled' | 'width' | 'direction' | 'nopanels' | ''

const urlParamsToField = new Map<UrlParams, keyof TidalSandbox>([
  ['nokeybindings', 'disableKeybindings'],
  ['fs', 'fullScreen'],
  ['', 'globalControlsConfig'],
  ['nopanels', 'globalControlsDisabled'],
  ['collapsed', 'navigationCollapsed'],
  ['disabled', 'navigationDisabled'],
  ['width', 'navigationWidth'],
  ['direction', 'sidebarPosition'],
])
const fieldToUrlParams = new Map<keyof TidalSandbox, UrlParams>([
  ['disableKeybindings', 'nokeybindings'],
  ['fullScreen', 'fs'],
  ['globalControlsConfig', ''],
  ['globalControlsDisabled', 'nopanels'],
  ['navigationCollapsed', 'collapsed'],
  ['navigationDisabled', 'disabled'],
  ['navigationWidth', 'width'],
  ['sidebarPosition', 'direction'],
])

const defaultFieldsValues: TidalSandbox = {
  disableKeybindings: false,
  fullScreen: false,
  globalControlsConfig: {},
  globalControlsDisabled: false,
  navigationCollapsed: false,
  navigationDisabled: false,
  navigationWidth: 350,
  sidebarPosition: 'row-reverse',
}

// ------------------------------------------------------
// ------------------------------------------------------

const tidalSandboxStore = proxy<TidalSandbox>({ ...defaultFieldsValues })

// ------------------------------------------------------
// ------------------------------------------------------

function updateStateFromURL() {
  const href = window.location.href

  const urlParams = href
    .slice(href.indexOf('?') + 1)
    .split('&')
    .reduce<Partial<Record<keyof TidalSandbox, string>>>((acc, param) => {
      const [key, val] = param.split('=')
      const field = urlParamsToField.get(key as UrlParams)
      if (field !== undefined) {
        acc[field] = val
      }
      return acc
    }, {})

  for (const key of Object.keys(defaultFieldsValues)) {
    const field = key as keyof TidalSandbox

    const urlValue = urlParams[field]
    const useUrlValue = urlValue !== undefined

    switch (field) {
      case 'navigationDisabled':
      case 'disableKeybindings':
      case 'fullScreen':
      case 'navigationCollapsed':
      case 'globalControlsDisabled':
        tidalSandboxStore[field] = useUrlValue ? urlValue === 'true' : defaultFieldsValues[field]
        break

      case 'navigationWidth':
        tidalSandboxStore[field] = useUrlValue ? Number.parseInt(urlValue, 10) : defaultFieldsValues[field]
        break

      case 'sidebarPosition':
        tidalSandboxStore[field] = useUrlValue
          ? urlValue === 'row-reverse'
            ? 'row-reverse'
            : 'row'
          : defaultFieldsValues[field]
        break

      case 'globalControlsConfig':
      default:
        continue
    }
  }
}

updateStateFromURL()

function updateURLState() {
  let nextParams = ''

  for (const [key, value] of Object.entries(tidalSandboxStore)) {
    let urlParamValue = ''

    const field = key as keyof TidalSandbox

    switch (field) {
      case 'navigationDisabled':
      case 'disableKeybindings':
      case 'fullScreen':
      case 'navigationCollapsed':
      case 'globalControlsDisabled':
        urlParamValue = value ? 'true' : 'false'
        break

      case 'navigationWidth':
        urlParamValue = Math.round(value as TidalSandbox[typeof field]).toString()
        break

      case 'sidebarPosition':
        urlParamValue = value as TidalSandbox[typeof field]
        break

      case 'globalControlsConfig':
      default:
        continue
    }

    // skip default values
    if (defaultFieldsValues[field] === value) continue

    const urlParamKey = fieldToUrlParams.get(key as keyof TidalSandbox)
    nextParams = `${nextParams}&${urlParamKey}=${urlParamValue}`
  }

  // remove initial `&` character
  nextParams = nextParams.slice(1)
  if (nextParams.length > 0) nextParams = `?${nextParams}`

  let routerLocation = window.location.hash
  if (routerLocation.startsWith('#')) routerLocation = routerLocation.slice(1)

  const queryParamsIndex = routerLocation.indexOf('?')
  const rootLocation = queryParamsIndex !== -1 ? routerLocation.slice(0, queryParamsIndex) : routerLocation

  const nextLocation = `${rootLocation}${nextParams}`

  // use `replace` to avoid creating extra navigation states
  setRouterLocation?.(nextLocation, { replace: true })
}

subscribe(tidalSandboxStore, () => {
  updateURLState()
})

// ------------------------------------------------------
// ------------------------------------------------------

export function getTidalSandboxValue<Field extends keyof TidalSandbox>(field: Field) {
  return tidalSandboxStore[field]
}

export function setTidalSandboxValue<
  Field extends keyof TidalSandbox,
  Value extends TidalSandbox[Field] = TidalSandbox[Field],
>(field: Field, value: Value) {
  tidalSandboxStore[field] = value
}

export function updateTidalSandboxGlobalControlsConfig(
  configOrSetFunction: TidalSandboxGlobalControlsConfig | LazyResolver<TidalSandboxGlobalControlsConfig>,
) {
  const field = tidalSandboxStore.globalControlsConfig

  const config =
    typeof configOrSetFunction === 'function'
      ? configOrSetFunction(snapshot(tidalSandboxStore.globalControlsConfig))
      : configOrSetFunction

  for (const key of Object.keys(config)) {
    field[key as keyof typeof field] = {
      ...field[key as keyof typeof field],
      ...config[key as keyof typeof config],
    }
  }
}

export function setTidalSandboxGlobalControlsConfig(
  configOrSetFunction: TidalSandboxGlobalControlsConfig | LazyResolver<TidalSandboxGlobalControlsConfig>,
) {
  const config =
    typeof configOrSetFunction === 'function'
      ? configOrSetFunction(snapshot(tidalSandboxStore.globalControlsConfig))
      : configOrSetFunction

  for (const key of Object.keys(tidalSandboxStore.globalControlsConfig)) {
    delete tidalSandboxStore.globalControlsConfig[key as keyof TidalSandboxApi['setGlobalControlsConfig']]
  }

  Object.assign(tidalSandboxStore.globalControlsConfig, config)
}

// ------------------------------------------------------
// ------------------------------------------------------

export function useSyncRouterToSandboxState() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    updateStateFromURL()
    location
  }, [location])

  setRouterLocation = navigate
}

export function useTidalSandboxState() {
  return useSnapshot(tidalSandboxStore)
}

export function useSetTidalSandboxStateField<Field extends keyof TidalSandbox>(field: Field) {
  return useCallback(
    (valueOrSetFunction: TidalSandbox[Field] | ((prev: TidalSandbox[Field]) => TidalSandbox[Field])) => {
      setTidalSandboxValue(
        field,
        typeof valueOrSetFunction === 'function' ? valueOrSetFunction(getTidalSandboxValue(field)) : valueOrSetFunction,
      )
    },
    [field],
  )
}

export function useTidalSandboxStateField<Field extends keyof TidalSandbox>(
  field: Field,
): [value: TidalSandbox[Field], ReturnType<typeof useSetTidalSandboxStateField<Field>>] {
  const value = useSnapshot(tidalSandboxStore)[field]

  return [value, useSetTidalSandboxStateField<Field>(field)]
}
